/**
 * Loans Table Component
 * Displays list of active funding loans
 */
import type { LoanDetail } from '../../types/botMonitoring'
import { formatCurrency, formatRate, formatTimestamp } from '../../utils/formatters'
import LoadingSpinner from '../Common/LoadingSpinner'
import EmptyState from '../Common/EmptyState'

interface LoansTableProps {
  loans: LoanDetail[]
  currency: string
  isLoading?: boolean
  error?: Error | null
}

export default function LoansTable({ loans, currency, isLoading, error }: LoansTableProps) {
  // Loading state
  if (isLoading) {
    return <LoadingSpinner message="載入貸款資料..." />
  }

  // Error state
  if (error) {
    return (
      <EmptyState
        icon={
          <svg
            className="mx-auto h-12 w-12 text-red-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        }
        title="載入失敗"
        description="無法載入貸款資料，請稍後再試"
      />
    )
  }

  // Empty state
  if (loans.length === 0) {
    return <EmptyState title="目前無貸款" description="尚未有任何成功放貸的紀錄" />
  }

  // Helper function to check if loan is expiring soon (< 24 hours)
  const isExpiringSoon = (expiresAt: number) => {
    const now = Date.now()
    const timeLeft = expiresAt - now
    const hoursLeft = timeLeft / (1000 * 60 * 60)
    return hoursLeft < 24 && hoursLeft > 0
  }

  // Status text mapping
  const getStatusText = (status: string) => {
    const statusMap: Record<string, string> = {
      active: '進行中',
      closed: '已結清',
      returned: '已歸還',
    }
    return statusMap[status] || status
  }

  // Status color mapping
  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
      returned: 'bg-blue-100 text-blue-800',
    }
    return colorMap[status] || 'bg-gray-100 text-gray-800'
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              貸款 ID
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              金額
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              利率 (APR)
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              已賺利息
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              開始時間
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              到期時間
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              狀態
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {loans.map((loan) => {
            const expiringSoon = isExpiringSoon(loan.expires_at)
            return (
              <tr key={loan.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  #{loan.id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(loan.amount, currency)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                  {formatRate(loan.rate)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(loan.earned_interest, currency)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatTimestamp(loan.opened_at)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="flex items-center">
                    {expiringSoon && (
                      <svg
                        className="h-4 w-4 text-yellow-500 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    <span className={expiringSoon ? 'text-yellow-600 font-medium' : 'text-gray-500'}>
                      {formatTimestamp(loan.expires_at)}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                      loan.status
                    )}`}
                  >
                    {getStatusText(loan.status)}
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
